import { useEffect } from 'react'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Text } from 'blaze-ui/Text/Text'
import { Button } from 'blaze-ui/Button'
import { ArrowLeft, Close } from 'blaze-ui/icons'
import { FontPairingCard, FontPairingCardSkeleton } from 'components/FontPairingCard'
import { useFontPairingSelection } from '../../hooks'
import { trackEventInHeap, HeapEventType } from 'utils/heapTracking'
import Styles from '../../VisualStylesForExistingUsersModal.module.scss'

interface MobileFontSelectionStepProps {
  selectedFontPairingId: number | null
  onSelectFontPairing: (fontPairingId: number) => void
  onNext: () => void
  onBack: () => void
  onClose: () => void
  isLoading?: boolean
  recommendedIds?: number[]
}

export const MobileFontSelectionStep = ({
  selectedFontPairingId,
  onSelectFontPairing,
  onNext,
  onBack,
  onClose,
  isLoading: isLoadingRecommendations = false,
  recommendedIds,
}: MobileFontSelectionStepProps) => {
  const { displayedFontPairings, showLoading, handleSelectFontPairing } = useFontPairingSelection({
    selectedFontPairingId,
    onSelectFontPairing,
    recommendedIds,
    isLoadingRecommendations,
    skipAutoSelectIfCustomFonts: false, // Mobile doesn't have custom font UI
  })

  useEffect(() => {
    trackEventInHeap(HeapEventType.VisualStylesForExistingUsers, {
      action: 'view-font-pairing-step-mobile',
    })
  }, [])

  const handleNext = () => {
    trackEventInHeap(HeapEventType.VisualStylesForExistingUsers, {
      action: 'complete-font-pairing-step-mobile',
      font_pairing_id: selectedFontPairingId ?? undefined,
      recommended_font_pairing_ids: recommendedIds,
    })
    onNext()
  }

  return (
    <div className={Styles.mobileStepContainer}>
      <div className={Styles.mobileStepHeader}>
        <Button size="sm" variant="tertiary" onPress={onBack}>
          <ArrowLeft size={20} />
        </Button>
        <Button size="sm" variant="tertiary" onPress={onClose}>
          <Close size={20} />
        </Button>
      </div>

      <div className={Styles.mobileStepContent}>
        <div className={Styles.mobileFontSelectionContent}>
          <div className={Styles.mobileFontSelectionHeader}>
            <Heading level={1}>Let's set your font style</Heading>
            <Text color="var(--dark-40)" variant="primary">
              Your choices here guide the style of all the content Blaze will create for you
            </Text>
          </div>

          <Heading level={4}>Choose the fonts for your social posts:</Heading>

          <div className={Styles.mobileFontList}>
            {showLoading && [1, 2, 3].map((i) => <FontPairingCardSkeleton key={i} />)}
            {!showLoading &&
              displayedFontPairings.map((fontPairing) => (
                <FontPairingCard
                  key={fontPairing.id}
                  fontPairing={fontPairing}
                  isSelected={selectedFontPairingId === fontPairing.id}
                  onSelect={() => handleSelectFontPairing(fontPairing.id)}
                />
              ))}
          </div>
        </div>
      </div>

      <div className={Styles.mobileFooter}>
        <Button
          variant="primary"
          size="lg"
          onPress={handleNext}
          isDisabled={!selectedFontPairingId}
          className={Styles.mobileFullWidthButton}
        >
          Continue
        </Button>
      </div>
    </div>
  )
}
